import { useState, FormEvent } from "react";
import { Mail } from "lucide-react";

/**
 * Lead magnet: "The Entrepreneur's Mortgage Playbook"
 *
 * Posts to the HighLevel native form endpoint.
 *
 * LEAD_MAGNET_FORM_ID and LEAD_MAGNET_LOCATION_ID are the live HighLevel
 * form and location IDs. Submissions land in HighLevel and trigger the
 * automation attached to that form (welcome email with the playbook PDF,
 * tag, pipeline, etc.). Do not change these IDs without updating the
 * HighLevel workspace to match.
 */
const LEAD_MAGNET_FORM_ID = "OyJcMFrGAmNhQRXdBBXb";
const LEAD_MAGNET_LOCATION_ID = "wJ8lNk6xgrcZrAKr5ppd";

const LeadMagnet = () => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("formId", LEAD_MAGNET_FORM_ID);
      fd.append("location_id", LEAD_MAGNET_LOCATION_ID);
      fd.append("first_name", firstName);
      fd.append("email", email);

      await fetch("https://backend.leadconnectorhq.com/forms/submit", {
        method: "POST",
        body: fd,
        mode: "no-cors",
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please email marquetta@freshvisionrealty.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 md:py-28 bg-[#F9F7F2]">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[0.7rem] md:text-[0.75rem] uppercase tracking-[0.25em] text-[hsl(var(--accent-strong))] font-semibold mb-4">
            Free Resource
          </p>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-[#0A192F] leading-tight">
            The Entrepreneur&apos;s Mortgage Playbook
          </h2>
          <p className="text-lg md:text-xl text-[#0A192F]/80 mb-4 leading-relaxed">
            How self-employed buyers and real estate investors get approved in
            Atlanta without the runaround.
          </p>
          <p className="text-base text-[#0A192F]/70 mb-10 max-w-2xl mx-auto leading-relaxed">
            Most mortgage advisors do not understand entrepreneur income. Marquetta
            does. She is a licensed real estate broker and Mortgage Advisor. This free guide covers how
            to structure your finances, what lenders actually look for, and how to
            position yourself for approval even with non-traditional income.
          </p>

          {submitted ? (
            <div className="bg-white border border-[#C5A059]/40 rounded-xl p-8 shadow-sm">
              <Mail className="w-10 h-10 text-[hsl(var(--accent-strong))] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-xl font-semibold text-[#0A192F] mb-2">
                Your playbook is on its way.
              </p>
              <p className="text-[#0A192F]/70">
                Check your email in the next few minutes.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-[#C5A059]/30 rounded-xl p-6 md:p-8 shadow-sm text-left"
            >
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="lm-first-name" className="block text-sm font-semibold text-[#0A192F] mb-1.5">
                    First Name
                  </label>
                  <input
                    id="lm-first-name"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-[#0A192F]/20 focus:border-[#C5A059] focus:outline-none focus:ring-2 focus:ring-[#C5A059]/30 text-[#0A192F]"
                    placeholder="Marquetta"
                  />
                </div>
                <div>
                  <label htmlFor="lm-email" className="block text-sm font-semibold text-[#0A192F] mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="lm-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-[#0A192F]/20 focus:border-[#C5A059] focus:outline-none focus:ring-2 focus:ring-[#C5A059]/30 text-[#0A192F]"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#C5A059] hover:bg-[#D4AF6A] text-[#0A192F] font-semibold py-3.5 rounded-lg transition-all duration-300 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Send Me the Playbook"}
              </button>

              {error && (
                <p className="text-red-700 text-sm mt-3 text-center" role="alert">
                  {error}
                </p>
              )}

              <p className="text-[11px] text-[#0A192F]/60 mt-4 text-center leading-relaxed">
                By submitting you agree to receive communications from Marquetta
                Bryant. Reply STOP to opt out at any time.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default LeadMagnet;
